import { ErrorResponse } from "@boi/backend-utils";
import { BulkCreateQuestionBankReqModel, ClassRequestModel, CommonResponse, CreateQuestionBankReqModel, LatestNewQuestionModel, LatestNewQuestionsResponse, LatestQuestionBankViewReq, QNPIdReq, QuestionBankResponse, QuestionBankViewReq, QuestionsCountModel, SubjectClassReqModel, SubjectPivotCount } from "@boi/shared-models";
import { Injectable } from "@nestjs/common";
import { InjectRepository } from "@nestjs/typeorm";
import { ILike, In, Repository } from "typeorm";
import { ChapterRepo } from "../ai-generated-qp/repo/chapter-repo";
import { ClassRepo } from "../ai-generated-qp/repo/class-repo";
import { SubjectRepo } from "../ai-generated-qp/repo/subject-repo";
import { TopicRepo } from "../ai-generated-qp/repo/topic-repo";
import { CreateQuestionAdapter } from "./dtos/create-question.adapter";
import { CollegeCodesAndSymbolsRepo } from "./repos/college-codes-symbols.repo";
import { CreateQuestionRepo } from "./repos/create-question-repo";
import { LanguagesRepo } from "../ai-generated-qp/repo/languages-repo";
import { CreateQuestion } from "./entities/create-question";
import { AuthenticationEntity } from "../authentications/entities/authentications.entity";
import { AbilityRepo } from "../ai-generated-qp/repo/ability-repo";
import { AbilityTypeRepo } from "../ai-generated-qp/repo/ability-type-repo";
import { ComplexityRepo } from "../ai-generated-qp/repo/complexity-repo";
import { QuestionTypeMasterRepo } from "../ai-generated-qp/repo/question-type-master-repo";
import { CompetencyCodeRepo } from "../ai-generated-qp/repo/competency-code-repo";
import { QnoCountReqModelDto } from "./dtos/qnos-count.dto";


@Injectable()
export class QuestionBankService {
  constructor(
    private qBankRepo: CreateQuestionRepo,
    private qBankAdapter: CreateQuestionAdapter,
    private CollegeCodesRepo: CollegeCodesAndSymbolsRepo,
    private classMasterRepo: ClassRepo,
    private subjectMasterRepo: SubjectRepo,
    private chapterMasterRepo: ChapterRepo,
    private topicMasterRepo: TopicRepo,
    private languageMasterRepo: LanguagesRepo,
    private abilityRepo: AbilityRepo,
    private abilityTypeRepo: AbilityTypeRepo,
    private complexityRepo: ComplexityRepo,
    private qTypeMasterRepo: QuestionTypeMasterRepo,
    private competencyCodeRepo: CompetencyCodeRepo,
    @InjectRepository(AuthenticationEntity)
    private userRepo: Repository<AuthenticationEntity>
  ) { }

  private async resolveUserId(username: string): Promise<number | null> {
    if (!username) return null;
    const user = await this.userRepo.findOne({ where: { userName: username } });
    return user?.id ?? null;
  }

  // async createQuestionBank(req: CreateQuestionBankReqModel): Promise<CommonResponse> {
  //   const entity = this.qBankAdapter.convertDtoToEntity(req);
  //   const saveData = await this.qBankRepo.save(entity);
  //   return new CommonResponse(true, 2553, 'Question Bank Created Successfully', saveData);
  // }

  async createQuestionBank(req: any[]): Promise<CommonResponse> {
    if (!Array.isArray(req) || req.length === 0) {
      return new CommonResponse(false, 400, 'No questions provided.');
    }

    const validReqs = req.filter(item => {
      const hasContent = item.question &&
        item.question.trim() !== '' &&
        item.question !== '<p><br></p>' &&
        item.question !== '<p></p>';
      return hasContent;
    });

    if (validReqs.length === 0) {
      return new CommonResponse(false, 400, 'No valid question content provided.');
    }

    console.log("Valid requests to create question", validReqs);

    const savedEntities = [];

    const firstReq = req[0];
    const creatorId = await this.resolveUserId(firstReq.userName);

    const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: firstReq.subject } });
    const sourceChapter = await this.chapterMasterRepo.findOne({ where: { id: firstReq.chapter } });

    const relatedSubjects = await this.subjectMasterRepo.find({
      where: { subjectParentId: sourceSubject?.subjectParentId || 0, class: firstReq.classId }
    });
    const subjectIds = relatedSubjects.map(s => s.id);
    if (!subjectIds.includes(firstReq.subject)) subjectIds.push(firstReq.subject);

    const lastQ = await this.qBankRepo.createQueryBuilder('q')
      .where('q.class = :classId', { classId: firstReq.classId })
      .andWhere('q.subject IN (:...subjectIds)', { subjectIds })
      .orderBy('q.qno', 'DESC')
      .getOne();

    const newQno = lastQ ? lastQ.qno + 1 : 0;

    for (const item of validReqs) {
      let localizedSubjectId = item.subject;
      let localizedChapterId = item.chapter;

      if (sourceSubject && sourceSubject.subjectParentId > 0) {
        const localizedSubject = await this.subjectMasterRepo.findOne({
          where: {
            subjectParentId: sourceSubject.subjectParentId,
            language: item.language,
            class: item.classId
          }
        });
        if (localizedSubject) {
          localizedSubjectId = localizedSubject.id;
        }
      }

      if (sourceChapter && sourceChapter.chapterParentId > 0) {
        const localizedChapter = await this.chapterMasterRepo.findOne({
          where: {
            chapterParentId: sourceChapter.chapterParentId,
            subject: localizedSubjectId,
            class: String(item.classId)
          }
        });
        if (localizedChapter) {
          localizedChapterId = localizedChapter.id;
        }
      }

      const dto: CreateQuestionBankReqModel = {
        ...item,
        subject: localizedSubjectId,
        chapter: localizedChapterId,
        qno: item.qno || newQno,
        question: item.question || ''
      };

      const entity = this.qBankAdapter.convertDtoToEntity(dto, creatorId);
      savedEntities.push(await this.qBankRepo.save(entity));
    }

    return new CommonResponse(true, 2553, 'Questions Created Successfully', savedEntities);
  }
  // async updateQuestionBank(req: any): Promise<CommonResponse> {
  //   console.log(req, "r-----------");
  //   if (req.question_en_id) {
  //     await this.qBankRepo.update({ id: req.question_en_id }, { question: encodeURIComponent(req.questionEn) })
  //   }
  //   if (req.question_te_id) {
  //     await this.qBankRepo.update({ id: req.question_te_id }, { question: encodeURIComponent(req.questionTe) })
  //   }
  //   if (req.question_ur_id) {
  //     await this.qBankRepo.update({ id: req.question_ur_id }, { question: encodeURIComponent(req.questionUr) })
  //   }
  //   return new CommonResponse(true, 2554, "Question Updated Successfully");
  // }


  async updateQuestionBank(req: any): Promise<CommonResponse> {
    const savedEntities = [];
    const localizedUpdates = req.localizedQuestions || [];

    const updaterId = await this.resolveUserId(req.userName);

    for (const langUpdate of localizedUpdates) {
      if (!langUpdate.question && langUpdate.question !== '') continue;

      const commonFields = {
        marks: req.marks,
        complexity: req.complexity || 0,
        ability: req.ability,
        abilityType: req.abilityType,
        questiontype: req.questionTypeMaster || req.questiontype || 0,
        hasStimulus: req.hasStimulus,
        estimatedTime: req.estimatedTime,
        competencyCode: req.competencyCode,
        updatedBy: updaterId,
        preparedBy: req.preparedBy,
        mobileNo: req.mobileNo,
        hasAnswerKey: req.hasAnswerKey || 'No',
        answerKey: req.hasAnswerKey === 'Yes' ? (langUpdate.answerKey || req.answerKey || null) : null,
      };

      const encodedQuestion = langUpdate.question;

      if (langUpdate.id) {
        await this.qBankRepo.update({ id: langUpdate.id }, {
          ...commonFields,
          question: encodedQuestion
        });
      } else if (langUpdate.langId && langUpdate.question) {
        const newQuestion = new CreateQuestion();
        Object.assign(newQuestion, {
          ...commonFields,
          createdBy: updaterId,
          class: req.classId,
          subject: langUpdate.subject,
          chapter: langUpdate.chapter,
          language: langUpdate.langId,
          qno: req.qno,
          question: encodedQuestion,
          isActive: true
        });
        const saved = await this.qBankRepo.save(newQuestion);
        savedEntities.push(saved);
      }
    }

    return new CommonResponse(true, 2554, "Question Updated Successfully", savedEntities);
  }


  //   async updateQuestionBank(req: any): Promise<CommonResponse> {
  //   // console.log(req, "r-----------");

  //   const sharedFields = {
  //     marks: req.marks,
  //   };

  //   const languageUpdates = [
  //     { id: req.question_en_id, question: req.questionEn },
  //     { id: req.question_te_id, question: req.questionTe },
  //     { id: req.question_ur_id, question: req.questionUr },
  //   ];

  //   for (const entry of languageUpdates) {
  //     if (entry.id) {
  //       await this.qBankRepo.update(
  //         { id: entry.id },
  //         {
  //           ...sharedFields,
  //           question: encodeURIComponent(entry.question),
  //         }
  //       );
  //     }
  //   }

  //   return new CommonResponse(true, 2554, "Question Updated Successfully");
  // }




  async getAllQuestionBankData(req: QuestionBankViewReq): Promise<QuestionBankResponse> {
    const savedData = await this.qBankRepo.getAllQuestionBankData(req);
    return new QuestionBankResponse(true, 123, 'All Questions retrieved Successfully', savedData);
  }

  async GetSingleQuestionBankData(req: QuestionBankViewReq): Promise<LatestNewQuestionsResponse> {
    try {
      const { rawData, availableLanguages } = await this.qBankRepo.GetSingleQuestionBankData(req);

      const groupedData = new Map<number, LatestNewQuestionModel>();

      for (const item of rawData) {
        if (!groupedData.has(item.qno)) {
          groupedData.set(item.qno, new LatestNewQuestionModel({
            id: item.question_id,
            qno: item.qno,
            marks: item.marks,
            className: item.className,
            isActive: item.isActive === 1,
            difficulty: item.difficulty,
            complexity: item.complexity,
            ability: item.ability,
            abilityType: item.abilityType,
            questionType: item.questionType,
            hasStimulus: item.hasStimulus,
            estimatedTime: item.estimatedTime,
            competencyCode: item.competencyCode,
            class_id: item.class_id,
            classId: item.class_id,
            translations: {},
            complexity_id: Number(item.complexity || 0),
            questionType_id: Number(item.questionType || item.questiontype || 0),
            preparedBy: item.preparedBy,
            mobileNo: item.mobileNo,
            hasAnswerKey: item.hasAnswerKey,
            answerKey: item.answerKey,
            approvalStatus: item.approvalStatus || 'PENDING',
            approvedBy: item.approvedBy,
            approvedDate: item.approvedDate
          } as any));
        }

        const model = groupedData.get(item.qno);
        const lang = item.langName || 'ENGLISH';

        model.translations[lang] = {
          id: item.question_id,
          question: item.question,
          subject: item.subjectName,
          chapter: item.chapterName,
          subject_id: item.subject_id,
          chapter_id: item.chapter_id,
          complexity_id: Number(item.complexity || 0),
          questionType_id: Number(item.questionType || item.questiontype || 0),
          hasAnswerKey: item.hasAnswerKey,
          answerKey: item.answerKey
        } as any;

        if (lang === 'ENGLISH' || !model.question) {
          model.question = item.question;
          model.subject = item.subjectName;
          model.chapter = item.chapterName;
          model.subject_id = item.subject_id;
          model.subjectId = item.subject_id;
          model.chapter_id = item.chapter_id;
          model.chapterId = item.chapter_id;
          model.hasAnswerKey = item.hasAnswerKey;
          model.answerKey = item.answerKey;
        }
      }

      const transformedData = Array.from(groupedData.values());
      return new LatestNewQuestionsResponse(true, 200, 'Questions retrieved successfully', transformedData, availableLanguages);
    } catch (error) {
      console.error('Error in service GetSingleQuestionBankData:', error);
      return new LatestNewQuestionsResponse(false, 500, 'Error retrieving questions', [], []);
    }
  }

  async getAllLatestQuestionBankData(req: LatestQuestionBankViewReq): Promise<any> {
    const newReq = new QuestionBankViewReq(Number(req.classId), Number(req.subject), Number(req.chapter), Number(req.marks), Number(req.language));
    newReq.activeOnly = req.activeOnly;
    return await this.GetSingleQuestionBankData(newReq);
  }

  async activateOrDeactivateQuestion(req: QNPIdReq): Promise<CommonResponse> {
    const find = await this.qBankRepo.findOne({ where: { id: req.qnpId } });
    if (find) {
      await this.qBankRepo.update({ id: req.qnpId }, { isActive: !find.isActive });
      return new CommonResponse(true, 125478, `${find.isActive ? "Deactivated Successfully" : "Activated Successfully"}`);
    } else {
      throw new ErrorResponse(123458, "Status Not Found");
    }
  }

  async approveQuestion(req: any): Promise<CommonResponse> {
    try {
      const qno = Number(req.qno);
      const classId = Number(req.classId);
      const subjectId = Number(req.subjectId ?? req.subject);
      const approvedBy = req.approvedBy || req.userName || null;
      const requestedStatus = String(req.approvalStatus || '').toUpperCase();
      const approvalStatus = requestedStatus === 'FINAL' ? 'FINAL'
        : requestedStatus === 'HOLD' ? 'HOLD'
          : requestedStatus === 'PENDING' ? 'PENDING'
            : 'APPROVED';
      const isActionedStatus = approvalStatus !== 'PENDING';

      // A question exists as one row per language, all sharing the same qno within a
      // family of sibling subjects - approve the whole family so translations stay in sync.
      let subjectIds = [subjectId];
      const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: subjectId } });
      if (sourceSubject) {
        const targetParentId = Number(sourceSubject.subjectParentId) > 0 ? Number(sourceSubject.subjectParentId) : sourceSubject.id;
        const relatedSubjects = await this.subjectMasterRepo.find({
          where: [
            { subjectParentId: targetParentId },
            { id: targetParentId }
          ]
        });
        subjectIds = relatedSubjects.map(s => s.id);
        if (!subjectIds.includes(subjectId)) subjectIds.push(subjectId);
      }

      await this.qBankRepo.update(
        { qno: qno, class: classId, subject: In(subjectIds) },
        {
          approvalStatus: approvalStatus,
          approvedBy: isActionedStatus ? approvedBy : null,
          approvedDate: isActionedStatus ? new Date() : null
        }
      );

      return new CommonResponse(true, 200, 'Question status updated successfully');
    } catch (error) {
      console.error('[ERROR] approveQuestion:', error);
      return new CommonResponse(false, 500, 'Error approving question', error.message);
    }
  }

  async getQuestionsCountSummery(req: ClassRequestModel): Promise<CommonResponse> {
    const data: QuestionsCountModel = await this.qBankRepo.getQuestionsCountSummery(req);
    return new CommonResponse(true, 125478, 'summery count retrieved successfully', data);
  }

  async getQuestionSubjectPivot(req: ClassRequestModel): Promise<CommonResponse> {
    const data: SubjectPivotCount[] = await this.qBankRepo.getQuestionSubjectPivot(req);
    return new CommonResponse(true, 125478, 'summery count retrieved successfully', data);
  }

  async getAllCollegeCodesAndSymbols(): Promise<CommonResponse> {
    const savedData = await this.CollegeCodesRepo.find();
    return new CommonResponse(true, 2244, 'College Codes And Symbols Retrived Successfully', savedData)
  }

  async getAllQuestionsCountsBySubject(req: SubjectClassReqModel): Promise<CommonResponse> {
    const savedData = await this.qBankRepo.getAllQuestionsCountsBySubject(req);
    return new CommonResponse(true, 125, 'All Questions retrieved Successfully', savedData);
  }

  async bulkCreateQuestionBank(req: BulkCreateQuestionBankReqModel): Promise<CommonResponse> {
    const results = [];
    const errors = [];

    // Cache for name -> ID mappings within this bulk request
    const classCache = new Map();
    const subjectCache = new Map(); // key: classId-subjectName
    const chapterCache = new Map(); // key: subjectId-chapterName
    const abilityCache = new Map();
    const abilityTypeCache = new Map(); // key: abilityId-typeName
    const complexityCache = new Map();
    const qTypeMasterCache = new Map();
    const competencyCache = new Map();

    for (let i = 0; i < req.questions.length; i++) {
        const item = req.questions[i];
        try {
            // 1. Resolve Class
            const className = item.className?.trim();
            if (!className) throw new Error(`Class name is missing`);
            let classId = classCache.get(className);
            if (!classId) {
                const classEnt = await this.classMasterRepo.findOne({ where: { class: ILike(className) } });
                if (!classEnt) throw new Error(`Class '${className}' not found`);
                classId = classEnt.id;
                classCache.set(className, classId);
            }

            // 2. Resolve Subject
            const subjectName = item.subjectName?.trim();
            if (!subjectName) throw new Error(`Subject name is missing`);
            const subKey = `${classId}-${subjectName}`;
            let subjectId = subjectCache.get(subKey);
            if (!subjectId) {
                // Filter by language: 2 (English) to ensure we find the correct English subject record for name matching
                const subEnt = await this.subjectMasterRepo.findOne({ 
                    where: { subject: ILike(subjectName), class: classId, language: 2 } 
                });
                if (!subEnt) throw new Error(`Subject '${subjectName}' not found (for English language) in Class '${className}'`);
                subjectId = subEnt.id;
                subjectCache.set(subKey, subjectId);
            }

            // 3. Resolve Chapter
            const chapterName = item.chapterName?.trim();
            if (!chapterName) throw new Error(`Chapter name is missing`);
            const chapKey = `${subjectId}-${chapterName}`;
            let chapterId = chapterCache.get(chapKey);
            if (!chapterId) {
                const chapEnt = await this.chapterMasterRepo.findOne({ 
                    where: { chapter: ILike(chapterName), subject: subjectId } 
                });
                if (!chapEnt) throw new Error(`Chapter '${chapterName}' not found for Subject '${subjectName}'`);
                chapterId = chapEnt.id;
                chapterCache.set(chapKey, chapterId);
            }

            // 4. Resolve Topic (Optional, fallback to 0)
            let topicId = 0;
            if (item.topicName) {
                const topicName = item.topicName?.trim();
                const topicEnt = await this.topicMasterRepo.findOne({ where: { topic: ILike(topicName), chapter: chapterId } });
                topicId = topicEnt ? topicEnt.id : 0;
            }

            // 5. Resolve Ability
            let abilityId = undefined;
            if (item.abilityName) {
                const abilityName = item.abilityName?.trim();
                abilityId = abilityCache.get(abilityName);
                if (!abilityId) {
                    const ent = await this.abilityRepo.findOne({ where: { type: ILike(abilityName) } });
                    if (ent) {
                        abilityId = ent.id;
                        abilityCache.set(abilityName, abilityId);
                    }
                }
            }

            // 6. Resolve Ability Type
            let abilityTypeId = undefined;
            if (item.abilityTypeName && abilityId) {
                const abilityTypeName = item.abilityTypeName?.trim();
                const atKey = `${abilityId}-${abilityTypeName}`;
                abilityTypeId = abilityTypeCache.get(atKey);
                if (!abilityTypeId) {
                    const ent = await this.abilityTypeRepo.findOne({ where: { types: ILike(abilityTypeName), abilityId: abilityId } });
                    if (ent) {
                        abilityTypeId = ent.id;
                        abilityTypeCache.set(atKey, abilityTypeId);
                    }
                }
            }

            // 7. Resolve Complexity (Difficulty)
            let complexityId = undefined;
            if (item.difficultyName) {
                const difficultyName = item.difficultyName?.trim();
                complexityId = complexityCache.get(difficultyName);
                if (!complexityId) {
                    const ent = await this.complexityRepo.findOne({ where: { complexity: ILike(difficultyName) } });
                    if (ent) {
                        complexityId = ent.id;
                        complexityCache.set(difficultyName, complexityId);
                    }
                }
            }

            // 8. Resolve Question Type Master
            let qTypeMasterId = undefined;
            if (item.questionTypeName) {
                const questionTypeName = item.questionTypeName?.trim();
                qTypeMasterId = qTypeMasterCache.get(questionTypeName);
                if (!qTypeMasterId) {
                    const ent = await this.qTypeMasterRepo.findOne({ where: { type: ILike(questionTypeName) } });
                    if (ent) {
                        qTypeMasterId = ent.id;
                        qTypeMasterCache.set(questionTypeName, qTypeMasterId);
                    }
                }
            }

            // 9. Resolve Competency Code
            let competencyId = undefined;
            if (item.competencyCode) {
                const compCode = String(item.competencyCode).trim();
                competencyId = competencyCache.get(compCode);
                if (!competencyId) {
                    const ent = await this.competencyCodeRepo.findOne({ where: { code: ILike(compCode) } });
                    if (ent) {
                        competencyId = ent.id;
                        competencyCache.set(compCode, competencyId);
                    }
                }
            }

            // Create payload for single create
            const payload: CreateQuestionBankReqModel = {
                classId: classId,
                subject: subjectId,
                chapter: chapterId,
                topic: topicId,
                marks: item.marks || 1,
                qno: 0, // Service handles latest qno
                questionType: 0, 
                difficultyLevel: complexityId || 0,
                question: item.questionEn || item.questionHi || "",
                questionEn: item.questionEn || "",
                questionTe: item.questionHi || "", // TE column is used for HI as well
                questionUr: "",
                language: 2, // Default to English for the main record, service splits it
                userName: req.userName,
                approvedBy: "",
                timeStamp: new Date().toISOString().slice(0, 19).replace("T", " "),
                etimeStamp: new Date().toISOString().slice(0, 19).replace("T", " "),
                eStatus: 0,
                ability: abilityId,
                abilityType: abilityTypeId,
                questionTypeMaster: qTypeMasterId,
                hasStimulus: item.hasStimulus || "No",
                estimatedTime: item.estimatedTime || 0,
                competencyCode: competencyId,
                preparedBy: item.preparedBy || "",
                mobileNo: item.mobileNo ? String(item.mobileNo) : ""
            };

            const saveRes = await this.createQuestionBank([payload]);
            if (saveRes.status) {
                results.push({ index: i, status: 'success' });
            } else {
                errors.push({ index: i, error: saveRes.internalMessage });
            }

        } catch (err) {
            errors.push({ index: i, error: err.message });
        }
    }

    return new CommonResponse(
        errors.length === 0,
        errors.length === 0 ? 200 : 207,
        errors.length === 0 ? 'Bulk Upload Successful' : 'Bulk Upload completed with some errors',
        { results, errors }
    );
  }


  async getAvailableQuestionsByChapterAndSubAbility(req: QnoCountReqModelDto) {
    const data = await this.qBankRepo.getAvailableQuestionsByChapterAndSubAbility(req)
    return new CommonResponse(true, 125, 'All Questions retrieved Successfully', data);
  }

  async getQuestionAbilityDistribution(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionAbilityDistribution(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question ability distribution retrieved successfully', data);
  }

  async getQuestionCountsReport(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionCountsReport(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question counts report retrieved successfully', data);
  }

  async getQuestionTypeDistribution(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionTypeDistribution(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question type distribution retrieved successfully', data);
  }

  async getLanguagesByChapter(chapterId: number): Promise<CommonResponse> {
    const chapter = await this.chapterMasterRepo.findOne({ where: { id: chapterId } });
    if (!chapter) {
      return new CommonResponse(false, 404, 'Chapter not found');
    }

    const targetParentId = chapter.chapterParentId > 0 ? chapter.chapterParentId : chapter.id;
    const allChapters = await this.chapterMasterRepo.find({
      where: [
        { chapterParentId: targetParentId },
        { id: targetParentId }
      ]
    });

    const subjectIds = allChapters.map(c => c.subject);
    if (subjectIds.length === 0) return new CommonResponse(true, 200, 'No related subjects found', []);

    const subjects = await this.subjectMasterRepo.find({
      where: { id: In(subjectIds) }
    });

    const languages = [...new Set(subjects.map(s => s.language))].filter(l => l);

    return new CommonResponse(true, 200, 'Languages retrieved successfully', languages);
  }

  async getSiblingsByQno(qno: number, classId: number, subjectId: number): Promise<CommonResponse> {
    try {
      const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: subjectId } });
      if (!sourceSubject) {
        return new CommonResponse(false, 404, 'Subject not found');
      }

      const targetParentId = Number(sourceSubject.subjectParentId) > 0 ? Number(sourceSubject.subjectParentId) : sourceSubject.id;
      const relatedSubjects = await this.subjectMasterRepo.find({
        where: [
          { subjectParentId: targetParentId },
          { id: targetParentId }
        ]
      });

      const languages = await this.languageMasterRepo.find({
        where: { id: In(relatedSubjects.map(s => s.language)) }
      });

      const subjectIds = relatedSubjects.map(s => s.id);

      const siblings = await this.qBankRepo.find({
        where: {
          qno: qno,
          class: classId,
          subject: In(subjectIds),
          isActive: true
        },
        order: { id: 'DESC' }
      });

      const finalSiblings = [];
      for (const subj of relatedSubjects) {
        const existing = siblings.find(s => s.subject === subj.id);
        const lang = languages.find(l => l.id === subj.language);

        const smartDecode = (text: string) => text || '';

        if (existing) {
          finalSiblings.push({
            ...existing,
            question: smartDecode(existing.question),
            languageName: lang?.language || 'Unknown'
          });
        } else {
          const sourceQuestion = siblings[0];
          let localizedChapterId = 0;
          if (sourceQuestion) {
            const sourceChapter = await this.chapterMasterRepo.findOne({ where: { id: sourceQuestion.chapter } });
            if (sourceChapter) {
              const localizedChapter = await this.chapterMasterRepo.findOne({
                where: {
                  chapterParentId: sourceChapter.chapterParentId > 0 ? sourceChapter.chapterParentId : sourceChapter.id,
                  subject: subj.id
                }
              });
              localizedChapterId = localizedChapter?.id || 0;
            }
          }

          finalSiblings.push({
            id: null,
            qno: qno,
            class: classId,
            subject: subj.id,
            language: subj.language,
            languageName: lang?.language || 'Unknown',
            chapter: localizedChapterId,
            question: ''
          });
        }
      }

      return new CommonResponse(true, 200, 'Siblings retrieved successfully', finalSiblings);
    } catch (error) {
      console.error('[ERROR] getSiblingsByQno:', error);
      return new CommonResponse(false, 500, 'Error retrieving siblings', error.message);
    }
  }
}
